import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { 
  FileText, CreditCard, Building2, Clock, CheckCircle2, AlertCircle,
  Search, Filter, Download, Plus, ChevronRight, TrendingUp, Loader2,
  DollarSign, Calendar, RefreshCw, XCircle, Users, User, ChevronDown, ChevronUp,
  Banknote, Wallet, Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Payment method icons mapping
const getPaymentMethodIcon = (method) => {
  const icons = {
    stripe_invoice: '💳',
    stripe_bank: '🏦',
    stripe_card: '💳',
    revolut_card: '💳',
    revolut_bank: '🏦',
    bank_transfer: '🏦',
    cheque: '📝',
    cash: '💵',
    other: '💰'
  };
  return icons[method] || '💰';
};

const formatPaymentMethod = (method) => {
  const names = {
    stripe_invoice: 'Stripe Invoice',
    stripe_bank: 'Stripe Bank',
    stripe_card: 'Stripe Card',
    revolut_card: 'Revolut Card',
    revolut_bank: 'Revolut Bank',
    bank_transfer: 'Bank Transfer',
    cheque: 'Cheque',
    cash: 'Cash',
    other: 'Other'
  };
  return names[method] || method?.replace(/_/g, ' ');
};

const ContractsPayments = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('my'); // 'my', 'all', or user_id
  const [selectedContract, setSelectedContract] = useState(null);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [expandedContract, setExpandedContract] = useState(null); // For inline payment history
  const [expandedPaymentHistory, setExpandedPaymentHistory] = useState([]);
  const [loadingExpandedHistory, setLoadingExpandedHistory] = useState(false);
  const [manualPayment, setManualPayment] = useState({
    amount_gbp: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'stripe_invoice',
    reference_number: '',
    notes: ''
  });
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchContracts();
    fetchUsers();
  }, [viewMode]);

  // Fetch payment history when a contract is selected
  useEffect(() => {
    if (selectedContract && showRecordPaymentModal) {
      fetchPaymentHistory(selectedContract.contract_id);
    }
  }, [selectedContract, showRecordPaymentModal]);

  // Fetch payment history for expanded row
  const fetchExpandedPaymentHistory = async (contractId) => {
    setLoadingExpandedHistory(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/contracts/${contractId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setExpandedPaymentHistory(data.payment_info?.payments || []);
      }
    } catch (err) {
      console.error('Failed to fetch payment history:', err);
    } finally {
      setLoadingExpandedHistory(false);
    }
  };

  // Toggle expanded row
  const toggleExpandedContract = (contractId) => {
    if (expandedContract === contractId) {
      setExpandedContract(null);
      setExpandedPaymentHistory([]);
    } else {
      setExpandedContract(contractId);
      fetchExpandedPaymentHistory(contractId);
    }
  };

  const fetchPaymentHistory = async (contractId) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/contracts/${contractId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data.payment_info?.payments || []);
      }
    } catch (err) {
      console.error('Failed to fetch payment history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/contracts?view=${viewMode}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (err) {
      console.error('Failed to fetch contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        setUsers(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedContract || !manualPayment.amount_gbp) return;
    
    setRecordingPayment(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contract_id: selectedContract.contract_id,
          ...manualPayment,
          amount_gbp: parseFloat(manualPayment.amount_gbp)
        })
      });

      if (response.ok) {
        // Refresh payment history and contracts
        await fetchPaymentHistory(selectedContract.contract_id);
        await fetchContracts();
        
        // Update the selected contract with new totals
        const updatedContracts = contracts.map(c => {
          if (c.contract_id === selectedContract.contract_id) {
            const newPaid = c.total_paid_gbp + parseFloat(manualPayment.amount_gbp);
            return {
              ...c,
              total_paid_gbp: newPaid,
              remaining_gbp: Math.max(0, c.total_value_gbp - newPaid)
            };
          }
          return c;
        });
        const updatedSelected = updatedContracts.find(c => c.contract_id === selectedContract.contract_id);
        if (updatedSelected) {
          setSelectedContract(updatedSelected);
        }
        
        // Reset form for next payment
        setManualPayment({
          amount_gbp: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: 'stripe_invoice',
          reference_number: '',
          notes: ''
        });
      }
    } catch (err) {
      console.error('Failed to record payment:', err);
    } finally {
      setRecordingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2, label: 'Paid' },
      partial: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: TrendingUp, label: 'Partial' },
      awaiting_payment: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, label: 'Awaiting Payment' },
      pending_setup: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertCircle, label: 'Pending Setup' },
      overdue: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, label: 'Overdue' },
      pending: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock, label: 'Pending' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contract.payment_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate summary stats
  const totalValue = contracts.reduce((sum, c) => sum + (c.total_value_gbp || 0), 0);
  const totalPaid = contracts.reduce((sum, c) => sum + (c.total_paid_gbp || 0), 0);
  const pendingPayments = contracts.filter(c => ['pending_setup', 'awaiting_payment', 'overdue'].includes(c.payment_status)).length;
  const overdueCount = contracts.filter(c => c.payment_status === 'overdue').length;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#A2FF00] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Contracts & Payments
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Track contract payments and manage invoices
            </p>
          </div>
          {/* View Toggle */}
          <div className="flex items-center gap-3">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-4 py-2 focus:border-[#A2FF00] outline-none`}
            >
              <option value="my">My Contracts</option>
              <option value="all">All Contracts</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#A2FF00]/20 rounded-lg">
                <FileText className="w-5 h-5 text-[#A2FF00]" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Total Contracts</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{contracts.length}</p>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Total Collected</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(totalPaid)}</p>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Pending</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pendingPayments}</p>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Overdue</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{overdueCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4 mb-6`}>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by company or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 ${isDark ? 'bg-[#0A0A0A] border-white/20' : 'bg-gray-50 border-gray-200'}`}
                />
              </div>
            </div>
            <div className="flex gap-2">
              {['all', 'paid', 'pending_setup', 'awaiting_payment', 'overdue'].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? 'bg-[#A2FF00] text-black' : ''}
                  size="sm"
                >
                  {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Contracts Table */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}>
                  <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-4`}>Company</th>
                  <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-4`}>Contract Value</th>
                  <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-4`}>Billing</th>
                  <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-4`}>Paid</th>
                  <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-4`}>Status</th>
                  <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-4`}>Signed</th>
                  <th className={`text-right text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-4`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContracts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <FileText className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No contracts found</p>
                    </td>
                  </tr>
                ) : (
                  filteredContracts.map((contract) => (
                    <React.Fragment key={contract.contract_id}>
                      <tr className={`border-t ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className="p-4">
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{contract.company_name}</p>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{contract.contact_email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className={`font-mono font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {formatCurrency(contract.total_value_gbp)}
                          </p>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            {contract.is_subscription ? (
                              <><RefreshCw className="w-3 h-3" /> Monthly</>
                            ) : (
                              <><FileText className="w-3 h-3" /> One-time</>
                            )}
                          </span>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => contract.payments_count > 0 && toggleExpandedContract(contract.contract_id)}
                            className={`text-left ${contract.payments_count > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                          >
                            <p className={`font-mono ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                              {formatCurrency(contract.total_paid_gbp)}
                            </p>
                            {contract.remaining_gbp > 0 && (
                              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {formatCurrency(contract.remaining_gbp)} remaining
                              </p>
                            )}
                            {contract.payments_count > 0 && (
                              <p className={`text-xs flex items-center gap-1 mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                <Receipt className="w-3 h-3" />
                                {contract.payments_count} payment{contract.payments_count !== 1 ? 's' : ''}
                                {expandedContract === contract.contract_id ? (
                                  <ChevronUp className="w-3 h-3" />
                                ) : (
                                  <ChevronDown className="w-3 h-3" />
                                )}
                              </p>
                            )}
                          </button>
                        </td>
                        <td className="p-4">
                          {getStatusBadge(contract.payment_status)}
                        </td>
                        <td className="p-4">
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatDate(contract.signed_at)}
                          </p>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedContract(contract);
                                setManualPayment(prev => ({ ...prev, amount_gbp: contract.remaining_gbp.toString() }));
                                setShowRecordPaymentModal(true);
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Record Payment
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/proposals/${contract.proposal_id}`)}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Payment History Row */}
                      {expandedContract === contract.contract_id && (
                        <tr className={`${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <td colSpan={7} className="p-0">
                            <div className={`p-4 border-l-4 ${isDark ? 'border-[#A2FF00]' : 'border-green-500'}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <Receipt className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  Payment History - {contract.company_name}
                                </h4>
                              </div>
                              
                              {loadingExpandedHistory ? (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 className="w-5 h-5 text-[#A2FF00] animate-spin" />
                                </div>
                              ) : expandedPaymentHistory.length === 0 ? (
                                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No payments recorded yet</p>
                              ) : (
                                <div className="space-y-2">
                                  {expandedPaymentHistory.map((payment, idx) => (
                                    <div 
                                      key={payment.entry_id || idx}
                                      className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <span className="text-xl">{getPaymentMethodIcon(payment.payment_method)}</span>
                                        <div>
                                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {formatCurrency(payment.amount_gbp)}
                                          </p>
                                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {formatPaymentMethod(payment.payment_method)}
                                            {payment.reference_number && ` • Ref: ${payment.reference_number}`}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                          {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric'
                                          }) : '-'}
                                        </p>
                                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                          {payment.recorded_by_name && `by ${payment.recorded_by_name}`}
                                        </p>
                                        {payment.notes && (
                                          <p className={`text-xs italic mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            &ldquo;{payment.notes}&rdquo;
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Record Payment Modal */}
      {showRecordPaymentModal && selectedContract && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Record Payment</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedContract.company_name}</p>
            </div>

            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              {/* Contract Summary */}
              <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Contract Value</p>
                    <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(selectedContract.total_value_gbp)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Paid</p>
                    <p className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                      {formatCurrency(selectedContract.total_paid_gbp)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Remaining</p>
                    <p className={`text-lg font-bold ${selectedContract.remaining_gbp > 0 ? (isDark ? 'text-orange-400' : 'text-orange-600') : (isDark ? 'text-[#A2FF00]' : 'text-green-600')}`}>
                      {formatCurrency(selectedContract.remaining_gbp)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              {loadingHistory ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-[#A2FF00] animate-spin" />
                </div>
              ) : paymentHistory.length > 0 && (
                <div>
                  <h3 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                    Payment History ({paymentHistory.length} payment{paymentHistory.length !== 1 ? 's' : ''})
                  </h3>
                  <div className={`rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'} overflow-hidden`}>
                    <table className="w-full text-sm">
                      <thead className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                        <tr>
                          <th className={`text-left p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                          <th className={`text-left p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Amount</th>
                          <th className={`text-left p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Method</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentHistory.map((pmt, idx) => (
                          <tr key={pmt.entry_id || idx} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                            <td className={`p-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {pmt.payment_date ? new Date(pmt.payment_date).toLocaleDateString('en-GB') : '-'}
                            </td>
                            <td className={`p-2 font-mono ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                              {formatCurrency(pmt.amount_gbp)}
                            </td>
                            <td className={`p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span className="mr-1">{getPaymentMethodIcon(pmt.payment_method)}</span>
                              {formatPaymentMethod(pmt.payment_method)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* New Payment Form */}
              <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-4`}>
                <h3 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-3`}>Add New Payment</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-1`}>Amount (GBP) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualPayment.amount_gbp}
                      onChange={(e) => setManualPayment({ ...manualPayment, amount_gbp: e.target.value })}
                      placeholder="0.00"
                      className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : ''}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-1`}>Date *</label>
                    <Input
                      type="date"
                      value={manualPayment.payment_date}
                      onChange={(e) => setManualPayment({ ...manualPayment, payment_date: e.target.value })}
                      className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : ''}`}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className={`block text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-1`}>Payment Method</label>
                  <select
                    value={manualPayment.payment_method}
                    onChange={(e) => setManualPayment({ ...manualPayment, payment_method: e.target.value })}
                    className={`w-full px-3 py-2 rounded-md border text-sm ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="stripe_invoice">Stripe Invoice</option>
                    <option value="stripe_bank">Stripe Bank Payment</option>
                    <option value="revolut_card">Revolut Card Payment</option>
                    <option value="revolut_bank">Revolut Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className={`block text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-1`}>Reference #</label>
                    <Input
                      value={manualPayment.reference_number}
                      onChange={(e) => setManualPayment({ ...manualPayment, reference_number: e.target.value })}
                      placeholder="TXN-123456"
                      className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : ''}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-1`}>Notes</label>
                    <Input
                      value={manualPayment.notes}
                      onChange={(e) => setManualPayment({ ...manualPayment, notes: e.target.value })}
                      placeholder="Optional"
                      className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : ''}`}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex gap-3`}>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRecordPaymentModal(false);
                  setPaymentHistory([]);
                }}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                onClick={handleRecordPayment}
                disabled={!manualPayment.amount_gbp || recordingPayment}
                className="flex-1 bg-[#A2FF00] text-black hover:bg-[#8EE000]"
              >
                {recordingPayment ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording...</>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Payment
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ContractsPayments;
